/**
 * Keeps an edit's free element in sight: the plan layers the View menu can
 * hide are a preference (`FloorPlanPreferences.layers`), and nobody can see,
 * or drag, an element in a hidden layer.
 */
import type { FloorPlanFreeElement } from '@kaiizen/kai-floor-plan';
import { useEffect, useRef } from 'react';
import { type EditorMutableState, elementLayer, showsElement } from '../editor-model';
import { getFloorPlanPreferences, updateFloorPlanPreferences } from '../preferences';
import { useFloorPlanEditorState } from './useFloorPlanEditorState';

const selectedFreeElement = ({ selection, shape }: EditorMutableState): FloorPlanFreeElement | undefined =>
  selection.kind === 'inner' || selection.kind === 'innerCorner' ? shape.freeElements?.[selection.index] : undefined;

/** Turn the plan layer `element` falls in back on, when the view menu hides it. */
function revealElement(element: FloorPlanFreeElement): void {
  const { layers } = getFloorPlanPreferences();
  if (showsElement(layers, element)) return;
  updateFloorPlanPreferences({ layers: { ...layers, [elementLayer(element)]: true } });
}

/**
 * Runs edits that leave a free element selected (the + menu, the inspector's
 * Type picker and not-built-in checkbox) so the element stays in sight: once
 * the edit lands (the editor state has moved on from the one it started
 * from), a plan layer hiding the selected element turns back on. State that
 * moves without such an edit reveals nothing.
 */
export function useKeepSelectedElementInSight(): (edit: () => void) => void {
  const { st } = useFloorPlanEditorState();
  const stateBeforeEditRef = useRef<EditorMutableState | null>(null);
  useEffect(() => {
    if (!stateBeforeEditRef.current || stateBeforeEditRef.current === st) return;
    stateBeforeEditRef.current = null;
    const element = selectedFreeElement(st);
    if (element) revealElement(element);
  }, [st]);
  return edit => {
    stateBeforeEditRef.current = st;
    edit();
  };
}
